import { ShopPayButton } from '@shopify/hydrogen-react'
import clsx from 'clsx'
import { FC, useEffect, useState } from 'react'

export type ShopPayCheckoutButtonProps = {
  variantId: string
  quantity: number
  storeDomain: string
}

// If the CDN script never loads (e.g. blocked by an ad/tracker blocker), stop showing the
// skeleton after this long and reveal the (possibly still-empty) button instead of hanging.
const READY_FALLBACK_TIMEOUT_MS = 5000

export const ShopPayCheckoutButton: FC<ShopPayCheckoutButtonProps> = ({
  variantId,
  quantity,
  storeDomain,
}) => {
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    const markReady = () => {
      if (!cancelled) setIsReady(true)
    }

    // The underlying <shop-pay-button> custom element lazy-loads its own script from
    // Shopify's CDN, so it renders empty for a moment — show a skeleton until it's defined.
    customElements.whenDefined('shop-pay-button').then(markReady)
    const fallback = setTimeout(markReady, READY_FALLBACK_TIMEOUT_MS)

    return () => {
      cancelled = true
      clearTimeout(fallback)
    }
  }, [])

  return (
    <div className='relative h-full w-full'>
      {!isReady && (
        <span className='rounded absolute inset-0 animate-pulse bg-gray' aria-hidden='true' />
      )}

      <ShopPayButton
        variantIdsAndQuantities={[{ id: variantId, quantity }]}
        storeDomain={storeDomain}
        channel='headless'
        className={clsx('h-full w-full max-w-[440px] xl:max-w-none', !isReady && 'invisible')}
      />
    </div>
  )
}
